import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, MessageCircle, Send, ArrowRight, Link2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InvitePage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  const shareUrl = `${window.location.origin}/join/${sessionId}`;
  const shareText = `Let's decide where to eat! Join my ForkYeah session: ${shareUrl}`;

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await axios.get(`${API}/sessions/${sessionId}`);
        setSession(response.data.session);
      } catch (error) {
        toast.error("Session not found");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
    fetchSession();
  }, [sessionId, navigate]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Couldn't copy");
    }
  };

  const shareViaWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
  };

  const shareViaSMS = () => {
    window.open(`sms:?body=${encodeURIComponent(shareText)}`, "_blank");
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "ForkYeah - Decide where to eat",
          text: "Join my ForkYeah session!",
          url: shareUrl,
        });
      } catch (error) {
        if (error.name !== "AbortError") {
          copyLink();
        }
      }
    } else {
      copyLink();
    }
  };

  if (loading) {
    return (
      <main className="app-container">
        <div className="flex-1 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-forkyeah-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </main>
    );
  }

  return (
    <main className="app-container">
      <div className="flex-1 flex flex-col px-6 py-6 overflow-y-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-forkyeah-accent/30 rounded-full mb-4">
            <Send className="w-8 h-8 text-forkyeah-primary" strokeWidth={2} />
          </div>
          <h1 className="text-2xl font-black text-forkyeah-main mb-2">
            Invite your people
          </h1>
          <p className="text-forkyeah-muted font-semibold text-sm">
            Decision done in 60 seconds.
          </p>
        </motion.div>

        {/* QR Code */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-4 shadow-lg mb-4"
        >
          <div className="flex justify-center">
            <div className="p-3 bg-forkyeah-background rounded-xl">
              <QRCodeSVG
                value={shareUrl}
                size={140}
                level="H"
                fgColor="#2D231F"
                data-testid="qr-code"
              />
            </div>
          </div>
        </motion.div>

        {/* Copy Link */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-6"
        >
          <button
            data-testid="copy-link-btn"
            onClick={copyLink}
            className={`w-full h-14 rounded-xl font-bold flex items-center justify-center gap-3 transition-all active:scale-[0.98] ${
              copied
                ? "bg-green-500 text-white"
                : "bg-white text-forkyeah-main border-2 border-[#FFE8DD]"
            }`}
          >
            {copied ? (
              <>
                <Check className="w-5 h-5" strokeWidth={2.5} />
                Copied!
              </>
            ) : (
              <>
                <Link2 className="w-5 h-5" strokeWidth={2.5} />
                Copy invite link
              </>
            )}
          </button>
        </motion.div>

        {/* Share Options */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-3 mb-6"
        >
          <p className="text-xs font-bold text-forkyeah-muted uppercase tracking-wider text-center">
            Or share via
          </p>
          <div className="flex gap-3">
            <button
              data-testid="share-whatsapp-btn"
              onClick={shareViaWhatsApp}
              className="flex-1 h-12 bg-[#25D366] text-white rounded-xl font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
            >
              <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
              WhatsApp
            </button>
            <button
              data-testid="share-sms-btn"
              onClick={shareViaSMS}
              className="flex-1 h-12 bg-blue-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
            >
              <Send className="w-5 h-5" strokeWidth={2.5} />
              iMessage
            </button>
          </div>
        </motion.div>

        {/* Session Info Summary */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="bg-forkyeah-background rounded-xl p-4 mb-6"
        >
          <div className="flex flex-wrap gap-2 justify-center text-xs font-bold text-forkyeah-muted">
            <span className="bg-white px-2 py-1 rounded-full">{session?.address || "Current location"}</span>
            <span className="bg-white px-2 py-1 rounded-full">{session?.cuisine || "All cuisines"}</span>
            <span className="bg-white px-2 py-1 rounded-full">
              {session?.price_range?.map((p) => "$".repeat(p)).join(" ")}
            </span>
          </div>
        </motion.div>

        {/* Continue Button */}
        <div className="mt-auto">
          <motion.button
            data-testid="continue-to-lobby-btn"
            onClick={() => navigate(`/lobby/${sessionId}`)}
            className="btn-primary gap-2"
            whileTap={{ scale: 0.95 }}
          >
            Continue to lobby
            <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
          </motion.button>
          <p className="text-center text-xs text-forkyeah-muted mt-3 font-medium">
            You'll wait for others before swiping starts
          </p>
        </div>
      </div>
    </main>
  );
}
